/**
 * PriceWithDiscount — the strikethrough discount display contract
 * (BL-022 beta pricing).
 *
 * Renders `currentPriceText` with the struck-through `listPriceText`
 * and an optional discount chip when a discount is active; renders the
 * plain price otherwise. Pure presentation: consumers pass already
 * translated/formatted strings (no i18n in @tonnie/ui) and the server
 * decides whether a discount exists (Non-Negotiable #9).
 */

import * as React from 'react';

import { cn } from '../lib/utils';

export interface PriceWithDiscountProps {
  /** Formatted current price, e.g. "€1.00". */
  currentPriceText: string;
  /** Formatted list price, shown struck-through when discounted. */
  listPriceText?: string;
  /** Translated chip label, e.g. "Beta price" or "−90%". Presence = discounted. */
  chipLabel?: string;
  /** Suffix rendered after the price, e.g. "/month". */
  periodText?: string;
  /** Translated disclaimer line under the price. */
  disclaimerText?: string;
  size?: 'md' | 'lg';
  className?: string;
  'data-testid'?: string;
}

export function PriceWithDiscount({
  currentPriceText,
  listPriceText,
  chipLabel,
  periodText,
  disclaimerText,
  size = 'lg',
  className,
  'data-testid': testId,
}: PriceWithDiscountProps): React.JSX.Element {
  const discounted = Boolean(chipLabel && listPriceText && listPriceText !== currentPriceText);

  return (
    <div className={cn('space-y-1', className)} data-testid={testId}>
      <div className="flex items-baseline gap-2 flex-wrap">
        {discounted && (
          <s
            className={cn(
              'text-muted-foreground font-medium',
              size === 'lg' ? 'text-lg' : 'text-sm'
            )}
            aria-hidden="true"
          >
            {listPriceText}
          </s>
        )}
        <span className={cn('font-bold', size === 'lg' ? 'text-3xl' : 'text-xl')}>
          {currentPriceText}
        </span>
        {periodText && <span className="text-muted-foreground">{periodText}</span>}
        {discounted && (
          <span className="inline-flex items-center rounded-full bg-primary/10 border border-primary/20 px-2 py-0.5 text-xs font-semibold text-primary-accessible">
            {chipLabel}
          </span>
        )}
      </div>
      {discounted && disclaimerText && (
        <p className="text-xs text-muted-foreground">{disclaimerText}</p>
      )}
    </div>
  );
}
